import { useState } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import KineticHero from "@/components/KineticHero";
import EditorialMarquee from "@/components/EditorialMarquee";
import ManifestoChapters from "@/components/ManifestoChapters";
import InteractiveChecklist from "@/components/InteractiveChecklist";
import AffiliateGearCard from "@/components/AffiliateGearCard";
import NewsletterSection from "@/components/NewsletterSection";
import { Reveal, Eyebrow } from "@/components/Motion";
import { CHECKLISTS } from "@/data/checklists";
import { GEAR } from "@/data/gear";
import { Feather, CalendarDays, ArrowRight } from "lucide-react";

const HERO_EVENING = "https://images.unsplash.com/photo-1528732262645-b06fa3a79c9e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";
const HERO_MORNING = "/hero-morning.jpg";
const FLY_IMG = "https://images.unsplash.com/photo-1724514415178-c87e1d00acd0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";
const HATCH_IMG = "/journal/salmon-flies.webp";

export default function Home() {
    const [heroImage] = useState(() => {
        try {
            const last = localStorage.getItem("troutstart_hero");
            const next = last === "morning" ? "evening" : "morning";
            localStorage.setItem("troutstart_hero", next);
            return next === "morning" ? HERO_MORNING : HERO_EVENING;
        } catch {
            return HERO_MORNING;
        }
    });

    return (
        <>
            <SEO
                title="Fly Fishing Guides, Checklists & Gear"
                description="Trout Start — interactive packing checklists, fly fishing field guides, hatch charts, and honest, field-tested gear picks for trout anglers."
            />
            <KineticHero
                tall
                badge
                image={heroImage}
                bright={heroImage === HERO_MORNING}
                eyebrow="TroutStart.com — Fly Fishing Field Guides"
                lines={[
                    { text: "Read the water." },
                    { text: "Pack with purpose.", accent: "teal" },
                    { text: "Hook more trout.", accent: "coral" },
                ]}
                sub="Checklists, tactics, and honest gear picks for fly anglers — built on cold mornings and hard-earned strikes."
            >
                <Link
                    to="/fly-fishing"
                    data-testid="hero-cta-fly-fishing"
                    className="rounded-full bg-teal px-7 py-3.5 font-sans text-sm font-semibold text-[#0B1320] hover:bg-coral hover:text-white transition-colors duration-300"
                >
                    Start with the Fly
                </Link>
                <Link
                    to="/checklists"
                    data-testid="hero-cta-checklists"
                    className="rounded-full border border-white/25 px-7 py-3.5 font-sans text-sm font-semibold text-slate-100 hover:border-teal hover:text-teal transition-colors duration-300"
                >
                    Build Your Checklist
                </Link>
            </KineticHero>

            <EditorialMarquee
                items={["Fly Fishing", "Streamer Craft", "Tailwater Reading", "Hatch Matching", "Wild Water", "Gear That Earns It"]}
            />

            <ManifestoChapters />

            <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24 sm:pb-36" data-testid="disciplines-section">
                <Reveal>
                    <Eyebrow>Start Here</Eyebrow>
                    <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-100">
                        Master the fly. Then match the hatch.
                    </h2>
                </Reveal>

                <div className="mt-12 grid md:grid-cols-2 gap-6">
                    {[
                        {
                            to: "/fly-fishing",
                            testid: "discipline-card-fly",
                            img: FLY_IMG,
                            icon: Feather,
                            tag: "The Quiet Art",
                            title: "Fly Fishing",
                            body: "Hatches, drifts, and the long line. Learn to read seams, match insects, and present a fly like it belongs there.",
                        },
                        {
                            to: "/journal/trout-hatch-calendar",
                            testid: "discipline-card-hatch",
                            img: HATCH_IMG,
                            icon: CalendarDays,
                            tag: "The Calendar",
                            title: "Hatch Calendar",
                            body: "Nine hatches, nine months, one timeline — when each bug is on the water, its peak weeks, and the exact pattern to tie on.",
                        },
                    ].map((d, i) => (
                        <Reveal key={d.to} delay={i * 0.12}>
                            <Link
                                to={d.to}
                                data-testid={d.testid}
                                className="group relative block rounded-2xl overflow-hidden border border-white/10 hover:border-teal/40 transition-colors duration-500"
                            >
                                <div className="aspect-[16/10] overflow-hidden">
                                    <img
                                        src={d.img}
                                        alt={d.title}
                                        loading="lazy"
                                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                                    />
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1320] via-[#0B1320]/40 to-transparent" />
                                <div className="absolute bottom-0 p-7 sm:p-9">
                                    <div className="flex items-center gap-2 text-teal">
                                        <d.icon size={16} />
                                        <span className="font-mono text-[11px] tracking-[0.28em] uppercase">{d.tag}</span>
                                    </div>
                                    <h3 className="mt-2 font-serif text-3xl sm:text-4xl font-semibold text-slate-50 flex items-center gap-3">
                                        {d.title}
                                        <ArrowRight size={24} className="text-teal opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500" />
                                    </h3>
                                    <p className="mt-3 font-body text-sm sm:text-base text-slate-300 max-w-md">{d.body}</p>
                                </div>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="relative overflow-hidden" data-testid="from-the-water-section">
                <div className="grid lg:grid-cols-2">
                    <div className="relative min-h-[320px] lg:min-h-[480px] overflow-hidden">
                        <img
                            src="/journal/creek-birch.webp"
                            alt="A rocky trout creek sliding through cedar forest — home water, Trout Start"
                            loading="lazy"
                            className="absolute inset-0 h-full w-full object-cover"
                            style={{ objectPosition: "50% 45%" }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0B1320]/60 hidden lg:block" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1320] via-transparent to-transparent lg:hidden" />
                    </div>
                    <div className="flex items-center px-6 sm:px-12 py-16 lg:py-24">
                        <Reveal>
                            <Eyebrow>From the Water</Eyebrow>
                            <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-100">
                                Built by anglers who still get up before the alarm.
                            </h2>
                            <p className="mt-5 font-body text-base leading-relaxed text-slate-400">
                                Trout Start began on misty mornings exactly like this one — two rods, a thermos, and a river that didn't care about our plans. Every guide, checklist, and gear pick on this site comes from days like these: cold hands, rising fish, and notes scribbled on the tailgate before the drive home.
                            </p>
                            <p className="mt-6 font-mono text-xs tracking-[0.28em] uppercase text-teal">— The Trout Start crew</p>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="bg-[#0D1626] border-y border-white/10 py-24 sm:py-32" data-testid="checklist-preview-section">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-5 gap-12 items-start">
                    <Reveal className="lg:col-span-2 lg:sticky lg:top-32">
                        <Eyebrow>Interactive Checklists</Eyebrow>
                        <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-100">
                            Tick it off. It remembers.
                        </h2>
                        <p className="mt-5 font-body text-base leading-relaxed text-slate-400">
                            Field-tested packing lists that save your progress right in the browser — no account, no fuss. Add your own items, copy the list to your phone, reset before the next trip.
                        </p>
                        <Link
                            to="/checklists"
                            data-testid="checklist-preview-link"
                            className="mt-8 inline-flex items-center gap-2 font-sans text-sm font-semibold text-teal hover:text-coral transition-colors"
                        >
                            See all three field checklists <ArrowRight size={16} />
                        </Link>
                    </Reveal>
                    <Reveal delay={0.15} className="lg:col-span-3">
                        <InteractiveChecklist checklist={CHECKLISTS[2]} />
                    </Reveal>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-36" data-testid="featured-gear-section">
                <Reveal>
                    <div className="flex flex-wrap items-end justify-between gap-6">
                        <div>
                            <Eyebrow>Field-Tested Kit</Eyebrow>
                            <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-100">
                                Gear we'd buy again tomorrow
                            </h2>
                        </div>
                        <Link
                            to="/gear"
                            data-testid="featured-gear-link"
                            className="inline-flex items-center gap-2 font-sans text-sm font-semibold text-teal hover:text-coral transition-colors"
                        >
                            Browse the full catalog <ArrowRight size={16} />
                        </Link>
                    </div>
                </Reveal>
                <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[GEAR[0], GEAR[3], GEAR[4]].map((item, i) => (
                        <AffiliateGearCard key={item.id} item={item} index={i} />
                    ))}
                </div>
            </section>

            <NewsletterSection />
        </>
    );
}
